import React, { useState, useCallback, useEffect } from 'react';
import { useApiClient } from '../../config/axios';

interface ComprehensiveCatalog {
  all_keys: Record<string, number>;
  data_catalog: {
    metrics: string[];
    levels: string[];
    attributes: string[];
    members: string[];
    renderers: string[];
    editors: string[];
    xtypes: string[];
    dimensions: string[];
  };
  key_values: Record<string, string[]>;
}

interface TemplateNode {
  id: string;
  type: 'object' | 'array' | 'string' | 'number' | 'boolean';
  key?: string;
  value?: any;
  children?: TemplateNode[];
  parent?: string;
  isCalculation?: boolean;
  formula?: string;
  isArrayItem?: boolean;
}

// Searchable Dropdown Component
function SearchableDropdown({
  value,
  options,
  onChange,
  placeholder = "Search and select...",
  allowCustom = true
}: {
  value: string;
  options: string[];
  onChange: (value: string) => void;
  placeholder?: string;
  allowCustom?: boolean;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState(value || '');
  const [filteredOptions, setFilteredOptions] = useState(options);

  useEffect(() => {
    const filtered = options.filter(option =>
      option.toLowerCase().includes(searchTerm.toLowerCase())
    ).slice(0, 20); // Limit to 20 results
    setFilteredOptions(filtered);
  }, [searchTerm, options]);

  useEffect(() => {
    setSearchTerm(value || '');
  }, [value]);

  const handleSelect = (option: string) => {
    setSearchTerm(option);
    onChange(option);
    setIsOpen(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    if (allowCustom) {
      onChange(e.target.value);
    }
    setIsOpen(true);
  };

  return (
    <div style={{ position: 'relative', minWidth: '150px' }}>
      <input
        type="text"
        value={searchTerm}
        onChange={handleInputChange}
        onFocus={() => setIsOpen(true)}
        onBlur={() => setTimeout(() => setIsOpen(false), 200)}
        placeholder={placeholder}
        style={{
          padding: '4px 8px',
          border: '1px solid #ccc',
          borderRadius: '4px',
          width: '100%',
          fontSize: '12px'
        }}
      />
      {isOpen && filteredOptions.length > 0 && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          right: 0,
          background: 'white',
          border: '1px solid #ccc',
          borderRadius: '4px',
          maxHeight: '200px',
          overflowY: 'auto',
          zIndex: 1000,
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
        }}>
          {filteredOptions.map((option, index) => (
            <div
              key={index}
              onClick={() => handleSelect(option)}
              style={{
                padding: '8px',
                cursor: 'pointer',
                borderBottom: '1px solid #eee',
                fontSize: '12px'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = '#f5f5f5'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
            >
              {option}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Visual Node Component
function VisualNode({
  node,
  onUpdate,
  onDelete,
  onAddChild,
  catalog,
  depth = 0
}: {
  node: TemplateNode;
  onUpdate: (id: string, updates: Partial<TemplateNode>) => void;
  onDelete: (id: string) => void;
  onAddChild: (parentId: string, childType: 'object' | 'array' | 'string') => void;
  catalog: ComprehensiveCatalog;
  depth?: number;
}) {
  const apiClient = useApiClient();
  const [isExpanded, setIsExpanded] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [tempValue, setTempValue] = useState(node.value || '');
  const [showSuggestions, setShowSuggestions] = useState(false);

  const handleSave = () => {
    onUpdate(node.id, {
      value: tempValue,
      formula: node.isCalculation ? tempValue : undefined
    });
    setIsEditing(false);
  };

  // Auto-populate formula when calculation mode is enabled
  const handleCalculationToggle = async (isCalculation: boolean) => {
    onUpdate(node.id, { isCalculation });

    if (isCalculation && node.key) {
      // Look for existing formulas in the repository for this key
      try {
        const response = await apiClient.get('/comprehensive-catalog');
        const catalog = response.data;

        // Search for common calculation patterns based on the key
        const commonFormulas = getCommonFormulasForKey(node.key, catalog);
        if (commonFormulas.length > 0) {
          setTempValue(commonFormulas[0]); // Auto-populate with the most common formula
        }
      } catch (error) {
        console.log('Could not fetch formulas:', error);
      }
    }
  };

  // Helper function to find common formulas for a key
  const getCommonFormulasForKey = (key: string, catalog: any) => {
    const commonFormulas: string[] = [];

    // Common calculation patterns based on key names
    if (key.includes('total') || key.includes('sum')) {
      commonFormulas.push('{value1} + {value2}', 'SUM({field})');
    } else if (key.includes('percent') || key.includes('rate')) {
      commonFormulas.push('{numerator} / {denominator} * 100', '{part} / {whole}');
    } else if (key.includes('average') || key.includes('avg')) {
      commonFormulas.push('SUM({field}) / COUNT({field})', 'AVG({field})');
    } else if (key.includes('diff') || key.includes('change')) {
      commonFormulas.push('{current} - {previous}', '{newValue} - {oldValue}');
    } else if (key.includes('ratio')) {
      commonFormulas.push('{numerator} / {denominator}');
    } else {
      // Generic calculation suggestions
      commonFormulas.push('{field1} + {field2}', '{value} * {multiplier}', 'SUM({field})');
    }

    return commonFormulas;
  };

  // Get value suggestions based on key
  const getValueSuggestionsForKey = (key: string) => {
    const suggestions: string[] = [];

    // Add common values from catalog if available
    if (catalog.key_values && catalog.key_values[key]) {
      suggestions.push(...catalog.key_values[key].slice(0, 10));
    }

    // Add contextual suggestions based on key type
    if (key === 'xtype') {
      suggestions.push(...catalog.data_catalog?.xtypes?.slice(0, 10) || []);
    } else if (key === 'dimension') {
      suggestions.push(...catalog.data_catalog?.dimensions?.slice(0, 10) || []);
    } else if (key === 'dataIndex') {
      suggestions.push(...[
        ...catalog.data_catalog?.metrics?.slice(0, 5) || [],
        ...catalog.data_catalog?.attributes?.slice(0, 5) || []
      ]);
    }

    return [...new Set(suggestions)]; // Remove duplicates
  };


  const getIcon = () => {
    switch (node.type) {
      case 'object': return '🏗️';
      case 'array': return '📋';
      case 'string': return '📝';
      case 'number': return '🔢';
      case 'boolean': return '☑️';
      default: return '❓';
    }
  };

  const getBgColor = () => {
    const colors = ['#f8f9fa', '#e3f2fd', '#f3e5f5', '#e8f5e8', '#fff3e0'];
    return colors[depth % colors.length];
  };

  return (
    <div style={{
      marginLeft: `${depth * 12}px`,
      marginBottom: '4px',
      background: getBgColor(),
      border: '1px solid #ddd',
      borderRadius: '4px',
      padding: '6px'
    }}>
      {/* Node Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '4px' }}>
        <span style={{ fontSize: '16px' }}>{getIcon()}</span>

        {/* Key Searchable Dropdown - only show for non-array items */}
        {!node.isArrayItem && (
          <>
            <SearchableDropdown
              value={node.key || ''}
              options={Object.keys(catalog.all_keys || {})}
              onChange={(value) => onUpdate(node.id, { key: value })}
              placeholder="Search for key..."
              allowCustom={true}
            />
            <span>:</span>
          </>
        )}

        {/* For array items, show a label instead */}
        {node.isArrayItem && (
          <span style={{ color: '#666', fontSize: '14px', fontStyle: 'italic' }}>Array Item</span>
        )}

        {/* Type Selector */}
        <select
          value={node.type}
          onChange={(e) => onUpdate(node.id, {
            type: e.target.value as any,
            children: e.target.value === 'object' || e.target.value === 'array' ? [] : undefined
          })}
          style={{ padding: '2px 6px', border: '1px solid #ccc', borderRadius: '3px', fontSize: '12px' }}
        >
          <option value="string">String</option>
          <option value="number">Number</option>
          <option value="boolean">Boolean</option>
          <option value="object">Object { }</option>
          <option value="array">Array []</option>
        </select>

        {/* Action Buttons */}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '2px' }}>
          {(node.type === 'object' || node.type === 'array') && (
            <>
              <button
                onClick={() => setIsExpanded(!isExpanded)}
                style={{
                  background: 'none',
                  border: '1px solid #ccc',
                  borderRadius: '3px',
                  padding: '1px 4px',
                  cursor: 'pointer',
                  fontSize: '10px'
                }}
              >
                {isExpanded ? '▼' : '▶'}
              </button>
              <button
                onClick={() => onAddChild(node.id, 'string')}
                style={{
                  background: '#4CAF50',
                  color: 'white',
                  border: 'none',
                  borderRadius: '3px',
                  padding: '2px 4px',
                  cursor: 'pointer',
                  fontSize: '10px'
                }}
              >
                +
              </button>
            </>
          )}
          <button
            onClick={() => onDelete(node.id)}
            style={{
              background: '#f44336',
              color: 'white',
              border: 'none',
              borderRadius: '3px',
              padding: '2px 4px',
              cursor: 'pointer',
              fontSize: '10px'
            }}
          >
            ✕
          </button>
        </div>
      </div>

      {/* Value Editor for non-container types */}
      {node.type !== 'object' && node.type !== 'array' && (
        <div style={{ marginBottom: '8px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <label>
              <input
                type="checkbox"
                checked={node.isCalculation || false}
                onChange={(e) => handleCalculationToggle(e.target.checked)}
              />
              Formula/Calculation
            </label>
          </div>

          {isEditing ? (
            <div style={{ marginTop: '8px', display: 'flex', gap: '8px' }}>
              {node.isCalculation ? (
                <textarea
                  value={tempValue}
                  onChange={(e) => setTempValue(e.target.value)}
                  placeholder="Enter formula: e.g., {field1} + {field2}"
                  style={{
                    flex: 1,
                    padding: '8px',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    minHeight: '60px',
                    fontFamily: 'monospace'
                  }}
                />
              ) : (
                <div style={{ flex: 1 }}>
                  <SearchableDropdown
                    value={tempValue}
                    options={getValueSuggestionsForKey(node.key || '')}
                    onChange={(value) => setTempValue(value)}
                    placeholder={`Enter ${node.type} value or search suggestions...`}
                    allowCustom={true}
                  />
                </div>
              )}
              <button onClick={handleSave} style={{
                background: '#4CAF50',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                padding: '8px 12px',
                cursor: 'pointer'
              }}>
                ✓
              </button>
              <button onClick={() => setIsEditing(false)} style={{
                background: '#f44336',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                padding: '8px 12px',
                cursor: 'pointer'
              }}>
                ✕
              </button>
            </div>
          ) : (
            <div
              onClick={() => {
                setTempValue(node.isCalculation ? node.formula || '' : node.value || '');
                setIsEditing(true);
              }}
              style={{
                marginTop: '8px',
                padding: '8px',
                background: node.isCalculation ? '#fff3cd' : 'white',
                border: '1px solid #ddd',
                borderRadius: '4px',
                cursor: 'pointer',
                minHeight: '20px',
                fontFamily: node.isCalculation ? 'monospace' : 'inherit'
              }}
            >
              {node.isCalculation ? (
                <div>
                  <small style={{ color: '#856404' }}>Formula:</small> {node.formula || 'Click to add formula'}
                </div>
              ) : (
                node.value || <span style={{ color: '#999' }}>Click to add value</span>
              )}
            </div>
          )}
        </div>
      )}

      {/* Quick Value Suggestions - Collapsible */}
      {isEditing && !node.isCalculation && getValueSuggestionsForKey(node.key || '').length > 0 && (
        <div style={{ marginTop: '4px' }}>
          <button
            onClick={() => setShowSuggestions(!showSuggestions)}
            style={{
              background: 'none',
              border: 'none',
              color: '#666',
              fontSize: '10px',
              cursor: 'pointer',
              padding: '2px 4px',
              textDecoration: 'underline'
            }}
          >
            {showSuggestions ? '▼' : '▶'} {getValueSuggestionsForKey(node.key || '').length} suggestions
          </button>
          {showSuggestions && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2px', maxHeight: '60px', overflowY: 'auto', marginTop: '2px' }}>
              {getValueSuggestionsForKey(node.key || '').slice(0, 6).map((suggestion, index) => (
                <button
                  key={index}
                  onClick={() => setTempValue(suggestion)}
                  style={{
                    background: '#e8f5e8',
                    border: '1px solid #4CAF50',
                    borderRadius: '8px',
                    padding: '1px 4px',
                    fontSize: '9px',
                    cursor: 'pointer',
                    maxWidth: '100px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                  title={suggestion}
                >
                  {suggestion}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Children */}
      {(node.type === 'object' || node.type === 'array') && isExpanded && node.children && (
        <div style={{ marginTop: '12px' }}>
          {node.children.map(child => (
            <VisualNode
              key={child.id}
              node={child}
              onUpdate={onUpdate}
              onDelete={onDelete}
              onAddChild={onAddChild}
              catalog={catalog}
              depth={depth + 1}
            />
          ))}
          {node.children.length === 0 && (
            <div style={{
              color: '#999',
              fontStyle: 'italic',
              padding: '8px',
              textAlign: 'center',
              border: '2px dashed #ddd',
              borderRadius: '4px'
            }}>
              Empty {node.type} - click "+ Add" to add items
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Main Visual Builder Component
function VisualTemplateBuilder({ catalog }: { catalog: ComprehensiveCatalog }) {
  const apiClient = useApiClient();
  const [templateName, setTemplateName] = useState('');
  const [nodes, setNodes] = useState<TemplateNode[]>([
    {
      id: 'root',
      type: 'object',
      key: '',
      children: []
    }
  ]);
  const [saveStatus, setSaveStatus] = useState('');
  const [showJSONPreview, setShowJSONPreview] = useState(false);

  const generateId = () => Math.random().toString(36).substr(2, 9);

  const findNode = (id: string, nodeList: TemplateNode[] = nodes): TemplateNode | null => {
    for (const node of nodeList) {
      if (node.id === id) return node;
      if (node.children) {
        const found = findNode(id, node.children);
        if (found) return found;
      }
    }
    return null;
  };

  const updateNode = useCallback((id: string, updates: Partial<TemplateNode>) => {
    const updateInNodes = (nodeList: TemplateNode[]): TemplateNode[] => {
      return nodeList.map(node => {
        if (node.id === id) {
          return { ...node, ...updates };
        }
        if (node.children) {
          return { ...node, children: updateInNodes(node.children) };
        }
        return node;
      });
    };
    setNodes(updateInNodes(nodes));
  }, [nodes]);

  const deleteNode = useCallback((id: string) => {
    if (id === 'root') return; // Can't delete root

    const deleteFromNodes = (nodeList: TemplateNode[]): TemplateNode[] => {
      return nodeList.filter(node => {
        if (node.id === id) return false;
        if (node.children) {
          node.children = deleteFromNodes(node.children);
        }
        return true;
      });
    };
    setNodes(deleteFromNodes(nodes));
  }, [nodes]);

  const addChild = useCallback((parentId: string, childType: 'object' | 'array' | 'string') => {
    const newChild: TemplateNode = {
      id: generateId(),
      type: childType,
      key: '',
      value: '',
      children: childType === 'object' || childType === 'array' ? [] : undefined,
      parent: parentId
    };

    const addToNodes = (nodeList: TemplateNode[]): TemplateNode[] => {
      return nodeList.map(node => {
        if (node.id === parentId && node.children) {
          return { ...node, children: [...node.children, newChild] };
        }
        if (node.children) {
          return { ...node, children: addToNodes(node.children) };
        }
        return node;
      });
    };
    setNodes(addToNodes(nodes));
  }, [nodes]);

  // Convert visual nodes to JSON
  const nodesToJSON = (nodeList: TemplateNode[]): any => {
    const result: any = {};

    nodeList.forEach(node => {
      // Skip nodes without keys only if they are array items
      if (!node.key && node.isArrayItem) return;
      // For regular nodes without keys, use a placeholder or skip if empty
      if (!node.key && !node.isArrayItem) {
        // Skip nodes that haven't been configured yet
        return;
      }

      if (node.type === 'object' && node.children) {
        result[node.key!] = nodesToJSON(node.children);
      } else if (node.type === 'array' && node.children) {
        result[node.key!] = node.children.map(child => {
          if (child.type === 'object' && child.children) {
            return nodesToJSON(child.children);
          }
          return child.isCalculation ?
            { formula: child.formula, eval: child.formula } :
            child.value;
        });
      } else {
        result[node.key!] = node.isCalculation ?
          { formula: node.formula, eval: node.formula } :
          node.value;
      }
    });

    return result;
  };

  const handleSave = async () => {
    if (!templateName.trim()) {
      setSaveStatus('❌ Please enter a template name');
      return;
    }

    const rootNode = nodes.find(n => n.id === 'root');
    if (!rootNode?.children?.length) {
      setSaveStatus('❌ Template is empty');
      return;
    }

    try {
      setSaveStatus('💾 Saving...');
      const jsonSpec = nodesToJSON(rootNode.children);

      await apiClient.put(`/templates/${templateName}`, {
        spec: jsonSpec,
        name: templateName,
        description: 'Visual builder template'
      });

      setSaveStatus('✅ Template saved successfully!');
      setTimeout(() => setSaveStatus(''), 3000);
    } catch (err) {
      setSaveStatus('❌ Failed to save template');
      console.error('Save failed:', err);
    }
  };

  // Complete template shells with real-world examples
  const getCompleteTemplateShell = (templateType: string) => {
    switch (templateType) {
      case 'grid':
        return {
          type: 'grid',
          adornments: ['stars', 'image', 'title'],
          main: {
            title: 'name',
            displayTitle: 'displayName',
            image: 'attribute:img:id',
            rowHeight: 30
          },
          view: [
            { text: 'Status', renderer: 'adornmentsGridRenderer', width: 30, dataIndex: 'id', pinned: 'left' },
            { text: 'Department', dataIndex: 'level:department:name', xtype: 'level', dimension: 'product' },
            { text: 'Class', dataIndex: 'level:class:name', xtype: 'level', dimension: 'product' },
            { text: 'Style', dataIndex: 'member:style:name', xtype: 'member', dimension: 'product' },
            { text: 'Units', dataIndex: 'metric:units:value', xtype: 'metric', dimension: 'measure' }
          ]
        };
      case 'groupBy':
        return {
          type: 'groupBy',
          default: 'level:class:name',
          hideEmptyRow: true,
          view: [
            { text: 'Department', dataIndex: 'level:department:name', xtype: 'level', dimension: 'product', groupingKey: 'level:department' },
            { text: 'Class', dataIndex: 'level:class:name', xtype: 'level', dimension: 'product', groupingKey: 'level:class' },
            { text: 'Style Count', dataIndex: 'metric:stylecount:value', xtype: 'metric', dimension: 'measure' },
            { text: 'Total Units', dataIndex: 'metric:units:value', xtype: 'metric', dimension: 'measure', aggregator: 'sum' }
          ]
        };
      case 'companionView':
        return {
          type: 'companionView',
          main: {
            stars: 'compositeattributeband',
            image: 'attribute:img:id',
            title: 'name',
            displayTitle: 'member:stylecolor:name',
            body: 'member:style:description'
          },
          view: [
            { text: 'Price', dataIndex: 'attribute:price:value', xtype: 'attribute', dimension: 'product' },
            { text: 'Category', dataIndex: 'level:category:name', xtype: 'level', dimension: 'product' }
          ]
        };
      case 'sort':
        return {
          type: 'sort',
          default: 'name',
          hideEmptyRow: true,
          view: [
            { text: 'Style Name', dataIndex: 'name', sortable: true },
            { text: 'Price', dataIndex: 'attribute:price:value', xtype: 'attribute', sortable: true },
            { text: 'Units Sold', dataIndex: 'metric:units:value', xtype: 'metric', sortable: true, sortDirection: 'desc' }
          ]
        };
      default:
        return { type: templateType, view: [] };
    }
  };

  // Helper function to create root node
  const createRootNode = (): TemplateNode => ({
    id: 'root',
    type: 'object',
    key: '',
    children: []
  });

  // Helper function to convert JSON to nodes
  const convertJSONToNodes = (jsonObj: any, parentId?: string): TemplateNode[] => {
    const nodes: TemplateNode[] = [];

    const convertValue = (key: string, value: any, parent?: string): TemplateNode => {
      const nodeId = generateId();

      if (Array.isArray(value)) {
        return {
          id: nodeId,
          type: 'array',
          key,
          parent,
          children: value.map((item, index) => {
            const childNode = convertValue('', item, nodeId);
            childNode.isArrayItem = true;
            return childNode;
          })
        };
      } else if (typeof value === 'object' && value !== null) {
        return {
          id: nodeId,
          type: 'object',
          key,
          parent,
          children: Object.entries(value).map(([k, v]) => convertValue(k, v, nodeId))
        };
      } else {
        return {
          id: nodeId,
          type: typeof value === 'number' ? 'number' : typeof value === 'boolean' ? 'boolean' : 'string',
          key,
          value,
          parent
        };
      }
    };

    Object.entries(jsonObj).forEach(([key, value]) => {
      nodes.push(convertValue(key, value, parentId));
    });

    return nodes;
  };

  // Enhanced template starters
  const loadTemplate = (templateType: string) => {
    const templateShell = getCompleteTemplateShell(templateType);
    const templateNodes: TemplateNode[] = convertJSONToNodes(templateShell);
    const rootNode = createRootNode();
    rootNode.children = templateNodes;
    setNodes([rootNode]);
  };

  // Save As functionality with JSON download
  const handleSaveAs = () => {
    const rootNode = nodes.find(n => n.id === 'root');
    const jsonData = rootNode?.children ? nodesToJSON(rootNode.children) : {};

    const dataStr = JSON.stringify(jsonData, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);

    const downloadLink = document.createElement('a');
    downloadLink.href = url;
    downloadLink.download = `${templateName || 'template'}.json`;
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
    URL.revokeObjectURL(url);

    setSaveStatus('✅ Downloaded!');
    setTimeout(() => setSaveStatus(''), 2000);
  };

  const rootNode = nodes.find(n => n.id === 'root');
  const previewJSON = rootNode?.children ? nodesToJSON(rootNode.children) : {};

  return (
    <div>
      <div style={{ marginBottom: '2rem', padding: '1rem', background: '#f8f9fa', borderRadius: '8px' }}>
        <h3>🎨 Visual Template Builder</h3>
        <p>Build templates visually with searchable keys/values, nested objects, and formula support!</p>

        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', marginTop: '1rem', flexWrap: 'wrap' }}>
          <input
            type="text"
            value={templateName}
            onChange={(e) => setTemplateName(e.target.value)}
            placeholder="Template Name"
            style={{ padding: '8px 12px', border: '1px solid #ccc', borderRadius: '4px', minWidth: '200px' }}
          />

          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            <button onClick={() => loadTemplate('grid')} style={{
              padding: '8px 12px',
              background: '#e3f2fd',
              border: '1px solid #90caf9',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px'
            }}>
              📊 Grid Template
            </button>
            <button onClick={() => loadTemplate('groupBy')} style={{
              padding: '8px 12px',
              background: '#f3e5f5',
              border: '1px solid #ce93d8',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px'
            }}>
              📋 GroupBy Template
            </button>
            <button onClick={() => loadTemplate('companionView')} style={{
              padding: '8px 12px',
              background: '#e8f5e8',
              border: '1px solid #a5d6a7',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px'
            }}>
              🖼️ Companion Template
            </button>
            <button onClick={() => loadTemplate('sort')} style={{
              padding: '8px 12px',
              background: '#fff3e0',
              border: '1px solid #ffb74d',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px'
            }}>
              🔢 Sort Template
            </button>
          </div>

          <button onClick={handleSave} style={{
            padding: '8px 16px',
            background: '#4CAF50',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontWeight: 'bold'
          }}>
            💾 Save Template
          </button>

          <button onClick={handleSaveAs} style={{
            padding: '8px 16px',
            background: '#2196F3',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontWeight: 'bold'
          }}>
            📥 Save As (Download JSON)
          </button>

          {saveStatus && <span style={{ fontWeight: 'bold', color: saveStatus.includes('✅') ? 'green' : 'red' }}>
            {saveStatus}
          </span>}
        </div>
      </div>

      <div style={{ display: 'flex', gap: '1rem' }}>
        {/* Visual Builder */}
        <div style={{ flex: showJSONPreview ? 2 : 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
            <h4>🛠️ Visual Builder</h4>
            <button
              onClick={() => setShowJSONPreview(!showJSONPreview)}
              style={{
                padding: '4px 8px',
                background: showJSONPreview ? '#4CAF50' : '#f0f0f0',
                color: showJSONPreview ? 'white' : '#333',
                border: '1px solid #ccc',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '12px'
              }}
            >
              {showJSONPreview ? '🙈 Hide JSON' : '👁️ Show JSON'}
            </button>
          </div>
          {rootNode && (
            <div>
              {rootNode.children?.map(node => (
                <VisualNode
                  key={node.id}
                  node={node}
                  onUpdate={updateNode}
                  onDelete={deleteNode}
                  onAddChild={addChild}
                  catalog={catalog}
                />
              ))}
              <button
                onClick={() => addChild('root', 'string')}
                style={{
                  width: '100%',
                  padding: '12px',
                  background: '#4CAF50',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '16px',
                  marginTop: '12px'
                }}
              >
                ➕ Add Root Property
              </button>
            </div>
          )}
        </div>

        {/* JSON Preview - Collapsible */}
        {showJSONPreview && (
          <div style={{ flex: 1, background: '#f8f9fa', padding: '0.5rem', borderRadius: '4px' }}>
            <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '14px' }}>📄 JSON Preview</h4>
            <pre style={{
              background: 'white',
              padding: '0.5rem',
              borderRadius: '4px',
              fontSize: '11px',
              maxHeight: '400px',
              overflow: 'auto',
              border: '1px solid #ddd',
              margin: 0
            }}>
              {JSON.stringify(previewJSON, null, 2)}
            </pre>
          </div>
        )}
      </div>
    </div>
  );
}

export default VisualTemplateBuilder;